// CTV-745: searchable single-select over the 210 Nielsen metros for the
// market rows. The plain <select> made finding a metro a scroll hunt; this
// filters by code or any part of the name as you type (the same matching the
// wizard's DMASearchSelector uses), with Enter picking the first open match.

import React from 'react';
import { Check, Search, X } from 'lucide-react';
import { DMA_REGIONS, type DMARegion } from '../../../../data/dmaList';
import { inputClass } from './publisherFieldStyles';

interface Props {
  /** Selected metro code, '' when the row has no metro yet. */
  value: string;
  onSelect: (dma: DMARegion | null) => void;
  /** Codes taken by other market rows: listed, but not selectable. */
  disabledCodes?: string[];
}

const MetroCombobox: React.FC<Props> = ({ value, onSelect, disabledCodes }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const selected = React.useMemo(
    () => DMA_REGIONS.find((d) => d.code === value),
    [value],
  );
  const taken = React.useMemo(() => new Set(disabledCodes ?? []), [disabledCodes]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return DMA_REGIONS;
    return DMA_REGIONS.filter(
      (d) => d.name.toLowerCase().includes(term) || d.code.includes(term),
    );
  }, [search]);

  const pick = (dma: DMARegion) => {
    if (taken.has(dma.code)) return;
    onSelect(dma);
    setOpen(false);
    setSearch('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      // The form has a submit button; a stray Enter here must pick a metro,
      // not submit the whole channel.
      e.preventDefault();
      const first = filtered.find((d) => !taken.has(d.code));
      if (open && first) pick(first);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label="Search metros"
        placeholder="Type a metro name or code"
        // While closed the input reads back the selection; typing searches.
        value={open ? search : selected ? `${selected.code} ${selected.name}` : ''}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setSearch('');
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        className={`${inputClass} pl-9 ${selected ? 'pr-8' : ''}`}
      />
      {selected && !open && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-label="Clear metro"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {open && (
        <div
          role="listbox"
          aria-label="Metro search results"
          className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {filtered.length > 0 ? (
            filtered.map((dma) => {
              const isTaken = taken.has(dma.code);
              const isSelected = dma.code === value;
              return (
                <button
                  key={dma.code}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={isTaken}
                  onClick={() => pick(dma)}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-2 ${
                    isTaken
                      ? 'text-slate-400 cursor-not-allowed'
                      : 'text-slate-900 hover:bg-slate-50'
                  } ${isSelected ? 'bg-slate-50' : ''}`}
                >
                  <span>
                    <span className="font-medium">{dma.code}</span>{' '}
                    <span className={isTaken ? 'text-slate-400' : 'text-slate-500'}>
                      {dma.name}
                    </span>
                  </span>
                  {isTaken && <span className="text-[10px] uppercase tracking-wide">added</span>}
                  {isSelected && <Check className="w-3.5 h-3.5 shrink-0 text-slate-700" />}
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">No metros found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MetroCombobox;
